import React, { useState, useEffect, useCallback } from 'react';
import { startOfMonth } from 'date-fns';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Stack,
  Grid2,
  CircularProgress,
  Button,
  TextField,
  InputAdornment,
  Alert,
} from '@mui/material';
import { Navigate } from 'react-router-dom';
import { Iconify } from '../Minimal/src/components/iconify';
import CommonDatePicker from '../datepicker/CommonDatePicker';
import config from '../common/Config';
import { isBritWholesaleUi } from '../../utils/britWholesaleUi';
import '../common/commonStyles.css';

const apiBase = () => String(config.API_URL || '').replace(/\/+$/, '');

const pad2 = (n) => String(n).padStart(2, '0');

const toYmd = (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;

const csvEscape = (v) => {
  const s = String(v ?? '');
  if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
};

const BritStockTransferReportInner = () => {
  const [dates, setDates] = useState(() => {
    const end = new Date();
    return [startOfMonth(end), end];
  });
  const [data, setData] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [appliedFrom, setAppliedFrom] = useState(() => {
    const end = new Date();
    return toYmd(startOfMonth(end));
  });
  const [appliedTo, setAppliedTo] = useState(() => toYmd(new Date()));
  const [listMessage, setListMessage] = useState('');
  const [error, setError] = useState('');
  const accessToken = localStorage.getItem('accessToken');

  const fetchReport = useCallback(async () => {
    if (!accessToken) return;
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams({
        date_from: appliedFrom,
        date_to: appliedTo,
        page: String(page + 1),
        limit: String(rowsPerPage),
        search: appliedSearch,
      });
      const res = await fetch(`${apiBase()}/inventory/ws-brit-stock-transfer-report?${params.toString()}`, {
        method: 'GET',
        headers: { authorization: `Bearer ${accessToken}` },
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) {
        setData([]);
        setTotalCount(0);
        setListMessage('');
        setError(json.message || json.error || 'Request failed.');
        return;
      }
      if (!json.success) {
        setData([]);
        setTotalCount(0);
        setListMessage('');
        setError(json.message || 'Request failed.');
        return;
      }
      const rows = Array.isArray(json.data) ? json.data : [];
      setData(rows);
      setListMessage(json.message || '');
      const meta = json.meta || {};
      const t = meta.total;
      setTotalCount(typeof t === 'number' && !Number.isNaN(t) ? t : rows.length);
    } catch {
      setData([]);
      setTotalCount(0);
      setListMessage('');
      setError('Network error.');
    } finally {
      setLoading(false);
    }
  }, [accessToken, page, rowsPerPage, appliedSearch, appliedFrom, appliedTo]);

  useEffect(() => {
    if (accessToken === null) {
      window.location.href = `${config.BASE_URL}/login`;
      return;
    }
    fetchReport();
  }, [accessToken, fetchReport]);

  const applyFiltersFromState = () => {
    if (dates[0] && dates[1]) {
      setAppliedFrom(toYmd(dates[0]));
      setAppliedTo(toYmd(dates[1]));
    }
  };

  const handleDateRangeChange = (value) => {
    setDates(Array.isArray(value) ? value : [null, null]);
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      setPage(0);
      applyFiltersFromState();
      setAppliedSearch(searchInput.trim());
    }
  };

  const handleSubmit = () => {
    setPage(0);
    applyFiltersFromState();
    setAppliedSearch(searchInput.trim());
  };

  const handleChangePage = (e, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (e) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  const exportCsv = () => {
    const headers = [
      'Date',
      'Order no.',
      'Product code',
      'Product name',
      'Order qty',
      'TES qty',
      'BRI qty',
      'Order type',
    ];
    const lines = [headers.map(csvEscape).join(',')];
    data.forEach((row) => {
      lines.push(
        [
          row.date ?? '',
          row.order_no ?? '',
          row.product_code ?? '',
          row.product_name ?? '',
          row.order_qty ?? '',
          row.tes_qty ?? '',
          row.bri_qty ?? '',
          row.order_type ?? '',
        ]
          .map(csvEscape)
          .join(',')
      );
    });
    const blob = new Blob([lines.join('\r\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'brit-stock-transfer-report.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Box className="page-container">
      <Paper className="paper-style">
        <Box sx={{ px: 2, py: 1.2 }}>
          <Typography className="page-title" mb={0.5}>
            Brit Stock Transfer Report
          </Typography>
          {/* {listMessage ? (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {listMessage}
            </Typography>
          ) : null} */}
          {error ? (
            <Alert severity="error" sx={{ mb: 1 }}>
              {error}
            </Alert>
          ) : null}
          <Grid2 container spacing={2} alignItems="flex-end">
            <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
              <CommonDatePicker
                className="input-border"
                value={dates}
                showOneCalendar
                onChange={handleDateRangeChange}
                format="dd/MM/yyyy"
                character=" - "
                style={{ width: '100%' }}
              />
            </Grid2>
            <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
              <TextField
                fullWidth
                className="input-border"
                size="small"
                placeholder="Search…"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={handleSearchKeyDown}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Iconify icon="eva:search-fill" sx={{ color: 'text.disabled' }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid2>
            <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
              <Stack direction="row" spacing={1}>
                <Button
                  className="button-variant-contained"
                  variant="contained"
                  color="primary"
                  onClick={handleSubmit}
                >
                  Search
                </Button>
                <Button
                  className="button-variant-outlined"
                  variant="outlined"
                  onClick={exportCsv}
                  disabled={!data.length}
                  startIcon={<Iconify icon="eva:download-outline" width={18} />}
                >
                  Export CSV
                </Button>
              </Stack>
            </Grid2>
          </Grid2>
        </Box>

        <TableContainer>
          <Table size="medium" className="table-cell-border">
            <TableHead>
              <TableRow className="table-cell-header">
                <TableCell sx={{ width: 56 }}>Sr.</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Order No.</TableCell>
              
                <TableCell sx={{ width: 480 }}>Product Name(Code)</TableCell>
                <TableCell align="center">Order Qty</TableCell>
                <TableCell align="center">C Qty</TableCell>
                <TableCell align="center">I Qty</TableCell>
                <TableCell>Type</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                    <CircularProgress size={24} />
                  </TableCell>
                </TableRow>
              ) : data.length > 0 ? (
                data.map((row, index) => (
                  <TableRow key={`${row.order_no || ''}-${row.product_code || ''}-${index}`} hover className="table-cell-style">
                    <TableCell align="center">{page * rowsPerPage + index + 1}</TableCell>
                    <TableCell>{row.date ?? '—'}</TableCell>
                    <TableCell>{row.order_no ?? '—'}</TableCell>
                    <TableCell>{row.product_name ?? '—'} ({row.product_code ?? '—'})</TableCell>
                    <TableCell align="center">{row.order_qty ?? '—'}</TableCell>
                    <TableCell align="center">{row.tes_qty ?? '—'}</TableCell>
                    <TableCell align="center">{row.bri_qty ?? '—'}</TableCell>
                    <TableCell>{row.order_type ?? '—'}</TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 4, color: '#637381', fontSize: '0.75rem' }}>
                    No records found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[10, 25, 50, 100]}
          labelRowsPerPage="Rows per page:"
          className="pagination-styles"
        />
      </Paper>
    </Box>
  );
};

const BritStockTransferReport = () => {
  if (!isBritWholesaleUi()) {
    return <Navigate to="/dashboard" replace />;
  }
  return <BritStockTransferReportInner />;
};

export default BritStockTransferReport;
